/**
 * PRD.305 — Laboratory reactive queries.
 *
 * ONE canonical dataset feeds every laboratory surface — the Laboratory module,
 * Patient Workspace Labs, Consultation Labs and the Files lock all read these
 * rows. Nothing here re-implements a second laboratory model.
 *
 * All pure logic (status ranking, aggregation, guards, capability resolution,
 * billing mapping) lives in `laboratory-workflow.ts` and is re-exported below,
 * so callers have a single import surface — the same shape `walk-in-services.ts`
 * uses for PRD.270.
 *
 * Org scoping is implicit: the local PowerSync database only ever contains the
 * current org's rows.
 */

import type { QuerySpec } from '@helios/powersync-client-core';
import { LAB_TEST_INVOICE_SOURCE_TYPE } from './laboratory-workflow.js';

export * from './laboratory-workflow.js';
export * from './laboratory-settings.js';
export * from './patient-file-guards.js';

// ─── Row shapes ───

/** A lab order plus its derived test rollup and patient identity. */
export interface LabOrderRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  encounter_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  ordering_provider_id: string | null;
  ordered_by: string | null;
  ordered_by_name: string | null;
  source: string | null;
  order_number: string | null;
  notes: string | null;
  /** PRD.305 — 'requested' | 'in_house' | 'external'. */
  fulfillment: string | null;
  fulfillment_at: string | null;
  fulfillment_by: string | null;
  ordered_at: string | null;
  cancelled_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Joined / derived
  patient_name: string | null;
  patient_number: string | null;
  provider_name: string | null;
  test_count: number | null;
  /** Comma-joined statuses of this order's tests — aggregated client-side. */
  test_statuses: string | null;
}

/** One ordered test, with its billing + result rollup. */
export interface LabOrderTestRow {
  id: string;
  org_id: string | null;
  lab_order_id: string | null;
  patient_id: string | null;
  service_id: string | null;
  test_name: string | null;
  test_code_snapshot: string | null;
  price_cents_snapshot: number | null;
  status: string | null;
  sort_order: number | null;
  collected_at: string | null;
  started_at: string | null;
  result_ready_at: string | null;
  published_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Settlement context from the EXISTING billing subsystem (display only). */
  invoice_payment_status: string | null;
  invoice_id: string | null;
  /** Result rollup. */
  result_count: number | null;
  published_result_count: number | null;
}

export interface LabResultRow {
  id: string;
  org_id: string | null;
  lab_order_test_id: string | null;
  patient_id: string | null;
  source: string | null;
  form_instance_id: string | null;
  summary: string | null;
  result_ready_at: string | null;
  published_at: string | null;
  published_by: string | null;
  published_by_name: string | null;
  corrects_id: string | null;
  correction_reason: string | null;
  superseded_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  file_count: number | null;
}

/** Joined artifact row — feeds `toLabResultArtifact`. */
export interface LabResultFileRow {
  id: string;
  lab_result_id: string | null;
  patient_file_id: string | null;
  filename: string | null;
  url: string | null;
  mime_type: string | null;
  sort_order: number | null;
  published_at: string | null;
}

/** A billable laboratory test from the service catalog. */
export interface LabTestCatalogRow {
  id: string;
  name: string | null;
  description: string | null;
  price_cents: number | null;
  category_id: string | null;
  category_name: string | null;
}

// ─── Shared SQL fragments ───

/**
 * Per-order test rollup. `test_statuses` is a comma-joined list rather than a
 * pre-computed aggregate: PRD §5.2 forbids persisting a second status, and
 * deriving it in one place (`aggregateLabOrderStatus`) keeps the SQL and the
 * unit-tested rule from ever disagreeing.
 */
const ORDER_ROLLUP = `
  (SELECT COUNT(*) FROM lab_order_tests t WHERE t.lab_order_id = o.id) AS test_count,
  (SELECT GROUP_CONCAT(t.status) FROM lab_order_tests t WHERE t.lab_order_id = o.id) AS test_statuses
`;

const ORDER_JOINS = `
  LEFT JOIN patients p ON p.id = o.patient_id
  LEFT JOIN appointment_providers ap ON ap.id = o.ordering_provider_id
`;

const ORDER_SELECT = `
  o.*,
  p.full_name      AS patient_name,
  p.patient_number AS patient_number,
  ap.display_name  AS provider_name,
  ${ORDER_ROLLUP}
`;

const ORDER_WATCH = ['lab_orders', 'lab_order_tests', 'patients', 'appointment_providers'] as const;

// ─── Laboratory module queries ───

/**
 * Every laboratory order for the org, newest first. The Laboratory queue
 * filters/sorts client-side through the pure helpers — all synced data is
 * already local, which is also what keeps the module usable offline.
 */
export function qLabOrders(): QuerySpec<[]> {
  return {
    key: ['lab_orders', 'all'] as const,
    sql: `
      SELECT ${ORDER_SELECT}
      FROM lab_orders o
      ${ORDER_JOINS}
      ORDER BY o.ordered_at DESC, o.created_at DESC
    `,
    params: [],
    watch: [...ORDER_WATCH],
  };
}

/** Laboratory orders for one patient — Patient Workspace Labs. */
export function qLabOrdersForPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['lab_orders', 'by-patient', patientId] as const,
    sql: `
      SELECT ${ORDER_SELECT}
      FROM lab_orders o
      ${ORDER_JOINS}
      WHERE o.patient_id = ?
      ORDER BY o.ordered_at DESC, o.created_at DESC
    `,
    params: [patientId],
    watch: [...ORDER_WATCH],
  };
}

/**
 * Laboratory orders raised during one consultation. Consultation Labs is
 * encounter-centric; orders created from the Patient Workspace carry no
 * encounter and correctly do not appear here.
 */
export function qLabOrdersForEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['lab_orders', 'by-encounter', encounterId] as const,
    sql: `
      SELECT ${ORDER_SELECT}
      FROM lab_orders o
      ${ORDER_JOINS}
      WHERE o.encounter_id = ?
      ORDER BY o.ordered_at DESC, o.created_at DESC
    `,
    params: [encounterId],
    watch: [...ORDER_WATCH],
  };
}

/** A single order header. */
export function qLabOrder(orderId: string): QuerySpec<[string]> {
  return {
    key: ['lab_orders', 'one', orderId] as const,
    sql: `
      SELECT ${ORDER_SELECT}
      FROM lab_orders o
      ${ORDER_JOINS}
      WHERE o.id = ?
      LIMIT 1
    `,
    params: [orderId],
    watch: [...ORDER_WATCH],
  };
}

/**
 * The tests of one order, with settlement context and a result rollup.
 *
 * Payment state is read from the existing `invoice_items.payment_status` — the
 * billing subsystem stays authoritative and Laboratory only displays what it
 * finds (PRD §11.5). Nothing in the laboratory flow is gated on it.
 */
export function qLabOrderTests(orderId: string): QuerySpec<[string]> {
  return {
    key: ['lab_order_tests', 'by-order', orderId] as const,
    sql: `
      SELECT
        t.*,
        ii.payment_status AS invoice_payment_status,
        ii.invoice_id     AS invoice_id,
        (SELECT COUNT(*) FROM lab_results r WHERE r.lab_order_test_id = t.id) AS result_count,
        (SELECT COUNT(*) FROM lab_results r
           WHERE r.lab_order_test_id = t.id AND r.published_at IS NOT NULL) AS published_result_count
      FROM lab_order_tests t
      LEFT JOIN invoice_items ii
        ON ii.source_type = '${LAB_TEST_INVOICE_SOURCE_TYPE}' AND ii.source_id = t.id
      WHERE t.lab_order_id = ?
      ORDER BY t.sort_order ASC, t.created_at ASC
    `,
    params: [orderId],
    watch: ['lab_order_tests', 'lab_results', 'invoice_items'],
  };
}

/** Every test for a patient across orders — the patient-centric history view. */
export function qLabTestsForPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['lab_order_tests', 'by-patient', patientId] as const,
    sql: `
      SELECT
        t.*,
        ii.payment_status AS invoice_payment_status,
        ii.invoice_id     AS invoice_id,
        (SELECT COUNT(*) FROM lab_results r WHERE r.lab_order_test_id = t.id) AS result_count,
        (SELECT COUNT(*) FROM lab_results r
           WHERE r.lab_order_test_id = t.id AND r.published_at IS NOT NULL) AS published_result_count
      FROM lab_order_tests t
      LEFT JOIN invoice_items ii
        ON ii.source_type = '${LAB_TEST_INVOICE_SOURCE_TYPE}' AND ii.source_id = t.id
      WHERE t.patient_id = ?
      ORDER BY t.created_at DESC, t.sort_order ASC
    `,
    params: [patientId],
    watch: ['lab_order_tests', 'lab_results', 'invoice_items'],
  };
}

// ─── Results & artifacts ───

/** Results for one test, newest first, with an artifact count. */
export function qLabResults(testId: string): QuerySpec<[string]> {
  return {
    key: ['lab_results', 'by-test', testId] as const,
    sql: `
      SELECT
        r.*,
        (SELECT COUNT(*) FROM lab_result_files f WHERE f.lab_result_id = r.id) AS file_count
      FROM lab_results r
      WHERE r.lab_order_test_id = ?
      ORDER BY r.created_at DESC
    `,
    params: [testId],
    watch: ['lab_results', 'lab_result_files'],
  };
}

/**
 * Published results for a patient — what `Published Results Only` (and a
 * disabled Laboratory module) still shows.
 */
export function qPublishedLabResultsForPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['lab_results', 'published-by-patient', patientId] as const,
    sql: `
      SELECT
        r.*,
        (SELECT COUNT(*) FROM lab_result_files f WHERE f.lab_result_id = r.id) AS file_count
      FROM lab_results r
      WHERE r.patient_id = ? AND r.published_at IS NOT NULL
      ORDER BY r.published_at DESC
    `,
    params: [patientId],
    watch: ['lab_results', 'lab_result_files'],
  };
}

/**
 * The artifacts of one result, joined to the real `patient_files` row.
 *
 * The join is what makes "one artifact, two surfaces" true: Labs renders the
 * same `patient_files.id`, filename and URL that the Files workspace does, so
 * there is never a second physical upload.
 */
export function qLabResultFiles(resultId: string): QuerySpec<[string]> {
  return {
    key: ['lab_result_files', 'by-result', resultId] as const,
    sql: `
      SELECT
        f.id              AS id,
        f.lab_result_id   AS lab_result_id,
        f.patient_file_id AS patient_file_id,
        f.sort_order      AS sort_order,
        pf.filename       AS filename,
        pf.url            AS url,
        pf.mime_type      AS mime_type,
        r.published_at    AS published_at
      FROM lab_result_files f
      LEFT JOIN patient_files pf ON pf.id = f.patient_file_id
      LEFT JOIN lab_results r ON r.id = f.lab_result_id
      WHERE f.lab_result_id = ?
      ORDER BY f.sort_order ASC, f.created_at ASC
    `,
    params: [resultId],
    watch: ['lab_result_files', 'patient_files', 'lab_results'],
  };
}

/** Every artifact belonging to a test, across all of its results. */
export function qLabResultFilesForTest(testId: string): QuerySpec<[string]> {
  return {
    key: ['lab_result_files', 'by-test', testId] as const,
    sql: `
      SELECT
        f.id              AS id,
        f.lab_result_id   AS lab_result_id,
        f.patient_file_id AS patient_file_id,
        f.sort_order      AS sort_order,
        pf.filename       AS filename,
        pf.url            AS url,
        pf.mime_type      AS mime_type,
        r.published_at    AS published_at
      FROM lab_result_files f
      JOIN lab_results r ON r.id = f.lab_result_id
      LEFT JOIN patient_files pf ON pf.id = f.patient_file_id
      WHERE r.lab_order_test_id = ?
      ORDER BY r.created_at DESC, f.sort_order ASC
    `,
    params: [testId],
    watch: ['lab_result_files', 'patient_files', 'lab_results'],
  };
}

// ─── Files integration (PRD §10, D4) ───

/**
 * `patient_files.id` values that are artifacts of a PUBLISHED laboratory
 * result, for one patient. Feeds `buildPatientFileLocks`, which every Files
 * surface consults before offering delete/edit.
 *
 * Scoped to published results only: a draft result's uploads are still ordinary
 * working files and stay fully editable.
 */
export function qLockedPatientFileIds(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_files', 'locks', patientId] as const,
    sql: `
      SELECT DISTINCT f.patient_file_id AS patient_file_id
      FROM lab_result_files f
      JOIN lab_results r ON r.id = f.lab_result_id
      WHERE f.patient_id = ? AND r.published_at IS NOT NULL
    `,
    params: [patientId],
    watch: ['lab_result_files', 'lab_results'],
  };
}

/**
 * Published laboratory artifacts for a patient, with enough linkage to name the
 * originating order and test — the provenance PRD §10 requires Files to keep.
 */
export function qPublishedLabFilesForPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['lab_result_files', 'published-by-patient', patientId] as const,
    sql: `
      SELECT
        f.id              AS id,
        f.lab_result_id   AS lab_result_id,
        f.patient_file_id AS patient_file_id,
        f.sort_order      AS sort_order,
        pf.filename       AS filename,
        pf.url            AS url,
        pf.mime_type      AS mime_type,
        r.published_at    AS published_at,
        t.id              AS lab_order_test_id,
        t.test_name       AS test_name,
        t.lab_order_id    AS lab_order_id
      FROM lab_result_files f
      JOIN lab_results r ON r.id = f.lab_result_id
      LEFT JOIN lab_order_tests t ON t.id = r.lab_order_test_id
      LEFT JOIN patient_files pf ON pf.id = f.patient_file_id
      WHERE f.patient_id = ? AND r.published_at IS NOT NULL
      ORDER BY r.published_at DESC, f.sort_order ASC
    `,
    params: [patientId],
    watch: ['lab_result_files', 'lab_results', 'lab_order_tests', 'patient_files'],
  };
}

// ─── Lab request pickup (PRD.305 — New Lab Order, Pharmacy-parity) ───
//
// The laboratory analog of Pharmacy's "Today's Prescriptions": orders a
// clinician raised that still have tests nobody has billed and settled. The
// shape and the queue rule deliberately mirror `qTodaysPrescriptions` — a
// request stays listed until every billable test is on a PAID invoice, so a
// billed-but-unpaid order does not silently vanish before payment is collected.
//
// "Invoiced" is tracked by the line's provenance, exactly as prescriptions do:
// `invoice_items.source_type = 'lab_test' AND source_id = <lab_order_tests.id>`.

/** One row per lab order, with billing progress counts. */
export interface LabRequestRow {
  lab_order_id: string;
  order_number: string | null;
  patient_id: string | null;
  patient_name: string | null;
  patient_number: string | null;
  ordered_at: string | null;
  provider_name: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  /** All tests on the order, including cancelled. */
  total_tests: number;
  /** Non-cancelled tests — the only ones that can be billed. */
  billable_tests: number;
  /** Billable tests placed on ANY invoice (draft or paid). */
  invoiced_tests: number;
  /** Billable tests placed on a PAID invoice. */
  paid_tests: number;
}

const LAB_REQUEST_SELECT = `
  o.id                AS lab_order_id,
  o.order_number      AS order_number,
  o.patient_id        AS patient_id,
  pt.full_name        AS patient_name,
  pt.patient_number   AS patient_number,
  o.ordered_at        AS ordered_at,
  o.encounter_id      AS encounter_id,
  o.visit_id          AS visit_id,
  COALESCE(prov.display_name, prov.full_name) AS provider_name,
  COUNT(t.id)         AS total_tests,
  SUM(CASE WHEN t.status != 'cancelled' THEN 1 ELSE 0 END) AS billable_tests,
  SUM(CASE WHEN t.status != 'cancelled' AND EXISTS(
        SELECT 1 FROM invoice_items ii
        WHERE ii.source_type = '${LAB_TEST_INVOICE_SOURCE_TYPE}' AND ii.source_id = t.id
      ) THEN 1 ELSE 0 END) AS invoiced_tests,
  SUM(CASE WHEN t.status != 'cancelled' AND EXISTS(
        SELECT 1 FROM invoice_items ii
        JOIN invoices i2 ON i2.id = ii.invoice_id
        WHERE ii.source_type = '${LAB_TEST_INVOICE_SOURCE_TYPE}' AND ii.source_id = t.id
          AND (i2.status = 'paid' OR (i2.status = 'issued' AND ii.payment_status = 'paid'))
      ) THEN 1 ELSE 0 END) AS paid_tests
`;

const LAB_REQUEST_JOINS = `
  FROM lab_orders o
  JOIN lab_order_tests t ON t.lab_order_id = o.id
  LEFT JOIN patients pt ON pt.id = o.patient_id
  LEFT JOIN appointment_providers prov ON prov.id = o.ordering_provider_id
`;

const LAB_REQUEST_WATCH = [
  'lab_orders',
  'lab_order_tests',
  'patients',
  'appointment_providers',
  'invoice_items',
  'invoices',
] as const;

/**
 * Today's lab requests that still have an unsettled billable test, across all
 * patients. `todayIso` is the caller's LOCAL YYYY-MM-DD — SQLite's `date('now')`
 * is UTC, so passing local today avoids day-boundary drift (same reason
 * `qTodaysPrescriptions` takes it).
 */
export function qTodaysLabRequests(todayIso: string): QuerySpec<[string]> {
  return {
    key: ['lab-requests', 'today', todayIso] as const,
    sql: `
      SELECT ${LAB_REQUEST_SELECT}
      ${LAB_REQUEST_JOINS}
      WHERE date(COALESCE(o.ordered_at, o.created_at)) = date(?)
        AND COALESCE(o.fulfillment, 'in_house') = 'in_house'
      GROUP BY o.id
      HAVING billable_tests > paid_tests
      ORDER BY o.ordered_at DESC
    `,
    params: [todayIso],
    watch: [...LAB_REQUEST_WATCH],
  };
}

/**
 * ALL of one patient's lab requests (any date), including fully settled ones,
 * so the drawer can SECTION them (today / earlier / already billed) instead of
 * hiding settled orders and reporting a misleading "none". Mirrors
 * `qPatientPrescriptions`.
 */
export function qPatientLabRequests(patientId: string): QuerySpec<[string]> {
  return {
    key: ['lab-requests', 'by-patient', patientId] as const,
    sql: `
      SELECT ${LAB_REQUEST_SELECT}
      ${LAB_REQUEST_JOINS}
      WHERE o.patient_id = ?
      GROUP BY o.id
      ORDER BY o.ordered_at DESC
      LIMIT 50
    `,
    params: [patientId],
    watch: [...LAB_REQUEST_WATCH],
  };
}

// ─── Catalog (PRD §11) ───

/**
 * The billable laboratory test catalog: active `appointment_services` marked
 * `is_lab_test`.
 *
 * The flag is the ONLY source of truth. Category is joined for display grouping
 * only — resolving the catalog by category name or a configured category id
 * would break the moment a clinic renames or reorganises its categories.
 */
export function qLabTestCatalog(): QuerySpec<[]> {
  return {
    key: ['appointment_services', 'lab-tests'] as const,
    sql: `
      SELECT
        s.id           AS id,
        s.name         AS name,
        s.description  AS description,
        s.price_cents  AS price_cents,
        s.category_id  AS category_id,
        c.name         AS category_name
      FROM appointment_services s
      LEFT JOIN appointment_categories c ON c.id = s.category_id
      WHERE s.is_lab_test = 1 AND s.active = 1
      ORDER BY s.name ASC
    `,
    params: [],
    watch: ['appointment_services', 'appointment_categories'],
  };
}
